/**
 * Page: Registration (Journey-Based)
 *
 * Page d'entrée pour tous les types de registration :
 * - Email OTP
 * - SMS OTP
 * - Password
 * - OIDC (Google/Apple)
 */

import { initPhoneInputs } from '@/_includes/components/phone-input.js';
import { authContext } from '@/scripts/utils/auth-context.js';
import { alert } from '@/scripts/utils/alert-message.js';
import { getDefaultMethodForPlatform } from '@/scripts/platform-detection.js';

document.addEventListener('DOMContentLoaded', async () => {
  await authContext.init();

  const currentJourney = authContext.getCurrentJourney();

  // ✅ Si aucun journey + déjà connecté → rediriger vers settings
  if (!currentJourney && authContext.isAuthenticated()) {
    window.location.href = '/settings.html';
    return;
  }

  // ============================================
  // JOURNEY VALIDATION (Entry Point Pattern)
  // ============================================
  // Si journey en cours mais pas de type REGISTRATION → clear + rester
  if (currentJourney && !currentJourney.name.startsWith('REGISTRATION')) {
    authContext.clearJourney();
    alert.info('#alert-container', 'Vous pouvez commencer une nouvelle inscription.', {
      icon: true,
      closable: true
    });
  }

  // Si journey REGISTRATION en cours : laisser les formulaires actifs
  // Si l'utilisateur submit → startJourney() écrasera l'ancien

  // Initialiser phone inputs
  initPhoneInputs();

  // UI Setup
  const tabButtons = document.querySelectorAll('[data-registration-tabs] [data-tab]');
  const tabContents = document.querySelectorAll('[data-tab-content]');

  const defaultMethod = getDefaultMethodForPlatform();

  function activateTab(tabName) {
    tabButtons.forEach((btn) => {
      const isActive = btn.dataset.tab === tabName;
      btn.setAttribute('aria-selected', isActive);
      btn.classList.toggle('border-primary-600', isActive);
      btn.classList.toggle('text-primary-600', isActive);
      btn.classList.toggle('border-transparent', !isActive);
      btn.classList.toggle('text-gray-500', !isActive);
    });

    tabContents.forEach((content) => {
      content.classList.toggle('hidden', content.dataset.tabContent !== tabName);
    });
  }

  activateTab(defaultMethod);

  tabButtons.forEach((button) => {
    button.addEventListener('click', () => activateTab(button.dataset.tab));
  });

  // Form Email OTP
  const emailForm = document.querySelector('form[data-registration-method="email"]');
  if (emailForm) {
    emailForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitButton = emailForm.querySelector('button[type="submit"]');

      submitButton.disabled = true;
      submitButton.textContent = 'Chargement...';

      try {
        const email = emailForm.querySelector('[name="email"]').value;
        const defaultFirstName = email.split('@')[0] || 'User';

        authContext.startJourney('REGISTRATION_EMAIL_OTP', 'email_input');

        authContext.save('identifier', email);
        authContext.save('method', 'email');
        authContext.save('defaultFirstName', defaultFirstName);

        await authContext.executeCurrentState();

      } catch (error) {
        console.error('[Registration] Error in email OTP flow:', error);
        submitButton.disabled = false;
        submitButton.textContent = 'Continuer';
      }
    });
  }

  // Form SMS OTP
  const smsForm = document.querySelector('form[data-registration-method="sms"]');
  if (smsForm) {
    smsForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitButton = smsForm.querySelector('button[type="submit"]');

      submitButton.disabled = true;
      submitButton.textContent = 'Chargement...';

      try {
        const phone = smsForm.querySelector('[name="phone"]').value;
        const defaultFirstName = 'User'; // Pour SMS, on n'a pas d'email pour extraire

        authContext.startJourney('REGISTRATION_SMS', 'account_creation');

        authContext.save('identifier', phone);
        authContext.save('method', 'sms');
        authContext.save('defaultFirstName', defaultFirstName);

        await authContext.executeCurrentState();

      } catch (error) {
        console.error('[Registration] Error in SMS flow:', error);
        submitButton.disabled = false;
        submitButton.textContent = 'Continuer';
      }
    });
  }

  // Form Password
  const passwordForm = document.querySelector('form[data-registration-method="password"]');
  if (passwordForm) {
    passwordForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitButton = passwordForm.querySelector('button[type="submit"]');

      submitButton.disabled = true;
      submitButton.textContent = 'Chargement...';

      try {
        const email = passwordForm.querySelector('[name="email"]').value;
        const password = passwordForm.querySelector('[name="password"]').value;
        const defaultFirstName = email.split('@')[0] || 'User';

        authContext.startJourney('REGISTRATION_PASSWORD', 'account_creation');

        authContext.save('identifier', email);
        authContext.save('password', password);
        authContext.save('method', 'password');
        authContext.save('defaultFirstName', defaultFirstName);

        await authContext.executeCurrentState();

      } catch (error) {
        console.error('[Registration] Error in password flow:', error);
        submitButton.disabled = false;
        submitButton.textContent = 'S\'inscrire';
      }
    });
  }

  // Social Buttons (OIDC)
  const socialButtons = document.querySelectorAll('[data-social-provider]');
  socialButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const provider = button.dataset.socialProvider;
      button.disabled = true;
      const originalHTML = button.innerHTML;
      button.innerHTML = '<span>Chargement...</span>';

      try {
        authContext.startJourney('LOGIN_OIDC', 'oidc_initiation');

        authContext.save('oidcProvider', provider);
        authContext.save('method', 'oidc');

        await authContext.executeCurrentState();

      } catch (error) {
        console.error('[Registration] Error in OIDC flow:', error);
        button.disabled = false;
        button.innerHTML = originalHTML;
      }
    });
  });
});
